import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Plus, Trash2, Save, Users, Edit, X, Check, Play, Pause, Square, Ban, UserPlus, Trophy, Calculator, Monitor, Copy, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '@/services/api';

export default function AdminPanel({ user }) {
  const navigate = useNavigate();
  const [hunts, setHunts] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingHunt, setEditingHunt] = useState(null);
  const [competitionResults, setCompetitionResults] = useState(null);
  const [endCompetitionData, setEndCompetitionData] = useState(null);

  // Race control state
  const [raceSettings, setRaceSettings] = useState({
    status: 'running',
    blocked_users: [],
    custom_users: [],
    wager_overrides: {},
    period_start: '',
    period_end: ''
  });
  const [newBlockUsername, setNewBlockUsername] = useState('');
  const [newCustomUser, setNewCustomUser] = useState({ username: '', wagered: '' });
  const [newOverride, setNewOverride] = useState({ username: '', wagered: '' });
  const [periodDates, setPeriodDates] = useState({ start: '', end: '' });
  const [timerInput, setTimerInput] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  const [newHunt, setNewHunt] = useState({
    status: 'preparing',
    start_balance: '',
    slots: [],
  });

  const [newSlot, setNewSlot] = useState({
    name: '',
    provider: '',
    bet_size: '',
  });

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    fetchAdminData();
  }, [user, navigate]);

  const fetchAdminData = async () => {
    try {
      const [huntsRes, compsRes, statsRes, usersRes, raceRes] = await Promise.all([
        api.get('/hunts'),
        api.get('/competitions'),
        api.get('/admin/stats'),
        api.get('/admin/users/all'),
        api.get('/admin/race'),
      ]);
      
      setHunts(huntsRes.data);
      setCompetitions(compsRes.data);
      setStats(statsRes.data);
      setUsers(usersRes.data || []);
      setRaceSettings(raceRes.data || {
        status: 'running',
        blocked_users: [],
        custom_users: [],
        wager_overrides: {},
        period_start: '',
        period_end: ''
      });
      // Set period dates for editing
      if (raceRes.data?.period_start) {
        setPeriodDates({
          start: raceRes.data.period_start,
          end: raceRes.data.period_end || ''
        });
      }
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      setError('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  // Race Control Functions
  const handleRaceStatus = async (status) => {
    try {
      await api.put(`/admin/race/status?status=${status}`);
      setSuccess(`Race ${status}!`);
      setRaceSettings({ ...raceSettings, status });
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to update race status');
    }
  };

  const handleUpdatePeriod = async () => {
    try {
      // Calculate deadline from timer input
      const totalSeconds = 
        (parseInt(timerInput.days) || 0) * 86400 +
        (parseInt(timerInput.hours) || 0) * 3600 +
        (parseInt(timerInput.minutes) || 0) * 60 +
        (parseInt(timerInput.seconds) || 0);
      
      await api.put('/admin/race/timer', {
        total_seconds: totalSeconds
      });
      setSuccess('Leaderboard timer updated!');
      fetchAdminData();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to update timer');
    }
  };

  const handleBlockUser = async () => {
    if (!newBlockUsername.trim()) return;
    try {
      await api.post(`/admin/race/block?username=${encodeURIComponent(newBlockUsername)}`);
      setSuccess(`User ${newBlockUsername} blocked`);
      setNewBlockUsername('');
      fetchAdminData();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to block user');
    }
  };

  const handleUnblockUser = async (username) => {
    try {
      await api.delete(`/admin/race/block/${encodeURIComponent(username)}`);
      setSuccess(`User ${username} unblocked`);
      fetchAdminData();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to unblock user');
    }
  };

  const handleAddCustomUser = async () => {
    if (!newCustomUser.username.trim() || !newCustomUser.wagered) return;
    try {
      await api.post(`/admin/race/custom-user?username=${encodeURIComponent(newCustomUser.username)}&wagered=${newCustomUser.wagered}`);
      setSuccess(`Custom user ${newCustomUser.username} added`);
      setNewCustomUser({ username: '', wagered: '' });
      fetchAdminData();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to add custom user');
    }
  };

  const handleDeleteCustomUser = async (userId) => {
    try {
      await api.delete(`/admin/race/custom-user/${userId}`);
      setSuccess('Custom user deleted');
      fetchAdminData();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to delete custom user');
    }
  };

  const handleSetOverride = async () => {
    if (!newOverride.username.trim() || !newOverride.wagered) return;
    try {
      await api.post(`/admin/race/override?username=${encodeURIComponent(newOverride.username)}&wagered=${newOverride.wagered}`);
      setSuccess(`Wager override set for ${newOverride.username}`);
      setNewOverride({ username: '', wagered: '' });
      fetchAdminData();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to set override');
    }
  };

  const handleRemoveOverride = async (username) => {
    try {
      await api.delete(`/admin/race/override/${encodeURIComponent(username)}`);
      setSuccess(`Override removed for ${username}`);
      fetchAdminData();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to remove override');
    }
  };

  // Hunt Functions
  const handleCreateHunt = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newHunt.slots.length === 0) {
      setError('Please add at least one slot to the hunt');
      return;
    }

    try {
      await api.post('/hunts', {
        status: newHunt.status,
        start_balance: parseFloat(newHunt.start_balance),
        slots: newHunt.slots.map(slot => ({
          ...slot,
          win: 0,
          multiplier: 0,
        })),
        opened_count: 0,
        collected_count: 0,
        best_win: 0,
        run_avg_x: 0,
      });
      setSuccess('Hunt created successfully!');
      setNewHunt({ status: 'preparing', start_balance: '', slots: [] });
      fetchAdminData();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to create hunt');
    }
  };

  const handleAddSlot = () => {
    if (!newSlot.name || !newSlot.provider || !newSlot.bet_size) {
      setError('Please fill in slot name, provider, and bet size');
      return;
    }
    
    const updatedSlots = [
      ...newHunt.slots,
      {
        name: newSlot.name,
        provider: newSlot.provider,
        bet_size: parseFloat(newSlot.bet_size) || 0,
        win: 0,
        multiplier: 0,
      },
    ];
    
    setNewHunt({ ...newHunt, slots: updatedSlots });
    setNewSlot({ name: '', provider: '', bet_size: '' });
    setError('');
  };

  const handleRemoveSlot = (index) => {
    const updatedSlots = newHunt.slots.filter((_, i) => i !== index);
    setNewHunt({ ...newHunt, slots: updatedSlots });
  };

  const handleEditHunt = (hunt) => {
    setEditingHunt({
      ...hunt,
      slots: hunt.slots.map(slot => ({ ...slot })),
      opened_count: hunt.opened_count || 0,
      collected_count: hunt.collected_count || 0
    });
  };

  const handleCancelEdit = () => {
    setEditingHunt(null);
  };

  const handleUpdateSlot = (index, field, value) => {
    const updatedSlots = [...editingHunt.slots];
    updatedSlots[index] = {
      ...updatedSlots[index],
      [field]: field === 'name' || field === 'provider' ? value : parseFloat(value) || 0
    };
    
    if (field === 'win' && updatedSlots[index].bet_size > 0) {
      updatedSlots[index].multiplier = parseFloat((parseFloat(value) / updatedSlots[index].bet_size).toFixed(2));
    }
    
    setEditingHunt({ ...editingHunt, slots: updatedSlots });
  };

  const handleSaveHunt = async () => {
    try {
      await api.put(`/hunts/${editingHunt.hunt_id}`, {
        status: editingHunt.status,
        start_balance: editingHunt.start_balance,
        slots: editingHunt.slots,
        opened_count: editingHunt.opened_count || 0,
        collected_count: editingHunt.collected_count || 0,
      });
      setSuccess('Hunt updated successfully!');
      setEditingHunt(null);
      fetchAdminData();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to update hunt');
    }
  };

  const handleAddSlotToExisting = () => {
    const updatedSlots = [
      ...editingHunt.slots,
      { name: '', provider: '', bet_size: 0, win: 0, multiplier: 0 }
    ];
    setEditingHunt({ ...editingHunt, slots: updatedSlots });
  };

  const handleRemoveSlotFromExisting = (index) => {
    const updatedSlots = editingHunt.slots.filter((_, i) => i !== index);
    setEditingHunt({ ...editingHunt, slots: updatedSlots });
  };

  const handleStartCompetition = async (huntId) => {
    try {
      await api.post('/competitions/start', { hunt_id: huntId });
      setSuccess('Competition started!');
      fetchAdminData();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to start competition');
    }
  };

  const handlePrepareEndCompetition = async (competition) => {
    // Get hunt to calculate end balance
    try {
      const huntRes = await api.get(`/hunts/${competition.hunt_id}/calculated-balance`);
      setEndCompetitionData({
        competition,
        hunt: huntRes.data,
        calculatedEndBalance: huntRes.data.calculated_end_balance,
        finalBalance: huntRes.data.calculated_end_balance.toString()
      });
    } catch (error) {
      // If endpoint fails, just use manual entry
      setEndCompetitionData({
        competition,
        hunt: null,
        calculatedEndBalance: null,
        finalBalance: ''
      });
    }
  };

  const handleEndCompetition = async () => {
    if (!endCompetitionData || !endCompetitionData.finalBalance) return;

    try {
      const response = await api.post(`/competitions/${endCompetitionData.competition.competition_id}/end`, { 
        final_balance: parseFloat(endCompetitionData.finalBalance) 
      });
      
      // Show results
      setCompetitionResults(response.data);
      setEndCompetitionData(null);
      fetchAdminData();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to end competition');
    }
  };

  const handleViewResults = async (competitionId) => {
    try {
      const response = await api.get(`/competitions/${competitionId}/results`);
      setCompetitionResults({
        final_balance: response.data.final_balance,
        winner: response.data.winner_username ? {
          username: response.data.winner_username,
          guess: response.data.winner_guess,
          difference: response.data.winner_difference
        } : null,
        total_guesses: response.data.guesses?.length || 0,
        all_results: response.data.guesses || []
      });
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to load results');
    }
  };

  const handlePauseCompetition = async (competitionId) => {
    try {
      await api.post(`/competitions/${competitionId}/pause`);
      setSuccess('Competition paused!');
      fetchAdminData();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to pause competition');
    }
  };

  const handleResumeCompetition = async (competitionId) => {
    try {
      await api.post(`/competitions/${competitionId}/resume`);
      setSuccess('Competition resumed!');
      fetchAdminData();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to resume competition');
    }
  };

  const [copiedOBS, setCopiedOBS] = useState(null);
  const handleCopyOBSLink = (huntId) => {
    const url = `${window.location.origin}/obs/bonus-hunt/${huntId}`;
    navigator.clipboard.writeText(url).catch(() => {});
    setCopiedOBS(huntId);
    setTimeout(() => setCopiedOBS(null), 2000);
  };

  const handleDeleteHunt = async (huntId) => {
    if (!window.confirm('Are you sure you want to delete this hunt?')) return;

    try {
      await api.delete(`/hunts/${huntId}`);
      setSuccess('Hunt deleted successfully!');
      fetchAdminData();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to delete hunt');
    }
  };

  if (!user?.is_admin) {
    return null;
  }

  if (loading) {
    return <div className="container mx-auto px-4 pt-32 pb-8">Loading...</div>;
  }

  const huntsList = hunts || [];
  const competitionsList = competitions || [];
  const slotsList = newHunt.slots || [];
  const usersList = users || [];
  const blockedUsers = raceSettings.blocked_users || [];
  const customUsers = raceSettings.custom_users || [];
  const wagerOverrides = raceSettings.wager_overrides || {};

  return (
    <div className="min-h-screen bg-[#0a0a0f] px-4 pt-32 pb-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-4xl font-bold text-yellow-500 mb-8">🔑 Admin Panel</h1>

        {error && (
          <Alert variant="destructive" className="mb-6 bg-red-900/50 border-red-700">
            <AlertDescription className="text-red-200">{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="mb-6 bg-green-900/50 border-green-700">
            <AlertDescription className="text-green-200">{success}</AlertDescription>
          </Alert>
        )}

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="bg-black/60 border-purple-500/30">
              <CardContent className="pt-6">
                <p className="text-sm text-gray-300">Total Users</p>
                <p className="text-3xl font-bold text-white">{stats.total_users}</p>
              </CardContent>
            </Card>
            <Card className="bg-black/60 border-purple-500/30">
              <CardContent className="pt-6">
                <p className="text-sm text-gray-300">Total Hunts</p>
                <p className="text-3xl font-bold text-white">{stats.total_hunts}</p>
              </CardContent>
            </Card>
            <Card className="bg-black/60 border-purple-500/30">
              <CardContent className="pt-6">
                <p className="text-sm text-gray-300">Total Guesses</p>
                <p className="text-3xl font-bold text-white">{stats.total_guesses}</p>
              </CardContent>
            </Card>
            <Card className="bg-black/60 border-purple-500/30">
              <CardContent className="pt-6">
                <p className="text-sm text-gray-300">Race Status</p>
                <p className={`text-2xl font-bold capitalize ${
                  raceSettings.status === 'running' ? 'text-green-500' : 
                  raceSettings.status === 'paused' ? 'text-yellow-500' : 'text-red-500'
                }`}>
                  {raceSettings.status}
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs defaultValue="race" className="space-y-6">
          <TabsList className="bg-black/60 border border-purple-500/30">
            <TabsTrigger value="race" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white text-gray-300">🏁 Race Control</TabsTrigger>
            <TabsTrigger value="hunts" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white text-gray-300">Bonus Hunts</TabsTrigger>
            <TabsTrigger value="competitions" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white text-gray-300">Guessing</TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white text-gray-300">
              <Users className="h-4 w-4 mr-2" />
              Users
            </TabsTrigger>
          </TabsList>

          {/* RACE CONTROL TAB */}
          <TabsContent value="race" className="space-y-6">
            {/* Race Status */}
            <Card className="bg-black/60 border-purple-500/30">
              <CardHeader>
                <CardTitle className="text-white">Race Status Control</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4 flex-wrap">
                  <Button 
                    onClick={() => handleRaceStatus('running')}
                    className={`${raceSettings.status === 'running' ? 'bg-green-600' : 'bg-gray-700'} text-white`}
                  >
                    <Play className="h-4 w-4 mr-2" /> Running
                  </Button>
                  <Button 
                    onClick={() => handleRaceStatus('paused')}
                    className={`${raceSettings.status === 'paused' ? 'bg-yellow-600' : 'bg-gray-700'} text-white`}
                  >
                    <Pause className="h-4 w-4 mr-2" /> Paused
                  </Button>
                  <Button 
                    onClick={() => handleRaceStatus('stopped')}
                    className={`${raceSettings.status === 'stopped' ? 'bg-red-600' : 'bg-gray-700'} text-white`}
                  >
                    <Square className="h-4 w-4 mr-2" /> Stopped
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Leaderboard Timer Control */}
            <Card className="bg-black/60 border-purple-500/30">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-yellow-500" />
                  Leaderboard Timer (Set Time Remaining)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label className="text-gray-300">Days</Label>
                    <Input
                      type="number"
                      min="0"
                      value={timerInput.days}
                      onChange={(e) => setTimerInput({ ...timerInput, days: e.target.value })}
                      className="bg-black/60 border-purple-500/30 text-white"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Hours</Label>
                    <Input
                      type="number"
                      min="0"
                      max="23"
                      value={timerInput.hours}
                      onChange={(e) => setTimerInput({ ...timerInput, hours: e.target.value })}
                      className="bg-black/60 border-purple-500/30 text-white"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Minutes</Label>
                    <Input
                      type="number"
                      min="0"
                      max="59"
                      value={timerInput.minutes}
                      onChange={(e) => setTimerInput({ ...timerInput, minutes: e.target.value })}
                      className="bg-black/60 border-purple-500/30 text-white"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Seconds</Label>
                    <Input
                      type="number"
                      min="0"
                      max="59"
                      value={timerInput.seconds}
                      onChange={(e) => setTimerInput({ ...timerInput, seconds: e.target.value })}
                      className="bg-black/60 border-purple-500/30 text-white"
                      placeholder="0"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Button onClick={handleUpdatePeriod} className="bg-purple-600 hover:bg-purple-700 text-white">
                    <Save className="h-4 w-4 mr-2" /> Set Timer
                  </Button>
                  <p className="text-gray-300 text-sm">
                    Set to: {timerInput.days}d {timerInput.hours}h {timerInput.minutes}m {timerInput.seconds}s
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Block Users */}
            <Card className="bg-black/60 border-purple-500/30">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Ban className="h-5 w-5 text-red-500" />
                  Blocked Users (Hidden from Leaderboard)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="Username to block"
                    value={newBlockUsername}
                    onChange={(e) => setNewBlockUsername(e.target.value)}
                    className="bg-black/60 border-purple-500/30 text-white placeholder:text-gray-500"
                  />
                  <Button onClick={handleBlockUser} variant="destructive">
                    <Ban className="h-4 w-4 mr-2" /> Block
                  </Button>
                </div>
                
                {blockedUsers.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {blockedUsers.map((username) => (
                      <div key={username} className="flex items-center gap-2 bg-red-900/30 border border-red-700 px-3 py-1 rounded-full">
                        <span className="text-red-400">{username}</span>
                        <button onClick={() => handleUnblockUser(username)} className="text-red-400 hover:text-white">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No blocked users</p>
                )}
              </CardContent>
            </Card>

            {/* Custom Users */}
            <Card className="bg-black/60 border-purple-500/30">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <UserPlus className="h-5 w-5 text-green-500" />
                  Custom Users (Add to Leaderboard)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="Username"
                    value={newCustomUser.username}
                    onChange={(e) => setNewCustomUser({ ...newCustomUser, username: e.target.value })}
                    className="bg-black/60 border-purple-500/30 text-white placeholder:text-gray-500"
                  />
                  <Input
                    type="number"
                    placeholder="Wager (C$)"
                    value={newCustomUser.wagered}
                    onChange={(e) => setNewCustomUser({ ...newCustomUser, wagered: e.target.value })}
                    className="bg-black/60 border-purple-500/30 text-white placeholder:text-gray-500 w-40"
                  />
                  <Button onClick={handleAddCustomUser} className="bg-green-600 hover:bg-green-700 text-white">
                    <Plus className="h-4 w-4 mr-2" /> Add
                  </Button>
                </div>
                
                {customUsers.length > 0 ? (
                  <div className="space-y-2">
                    {customUsers.map((cu) => (
                      <div key={cu.id} className="flex items-center justify-between bg-black/40 border border-purple-500/20 p-3 rounded">
                        <div>
                          <span className="font-semibold text-white">{cu.username}</span>
                          <span className="text-green-400 ml-4">C${cu.wagered}</span>
                        </div>
                        <Button onClick={() => handleDeleteCustomUser(cu.id)} variant="destructive" size="sm">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No custom users</p>
                )}
              </CardContent>
            </Card>

            {/* Wager Overrides */}
            <Card className="bg-black/60 border-purple-500/30">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Edit className="h-5 w-5 text-yellow-500" />
                  Wager Overrides (Change API Wager)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="Username (exact match)"
                    value={newOverride.username}
                    onChange={(e) => setNewOverride({ ...newOverride, username: e.target.value })}
                    className="bg-black/60 border-purple-500/30 text-white placeholder:text-gray-500"
                  />
                  <Input
                    type="number"
                    placeholder="New Wager (C$)"
                    value={newOverride.wagered}
                    onChange={(e) => setNewOverride({ ...newOverride, wagered: e.target.value })}
                    className="bg-black/60 border-purple-500/30 text-white placeholder:text-gray-500 w-40"
                  />
                  <Button onClick={handleSetOverride} className="bg-yellow-600 hover:bg-yellow-700 text-white">
                    <Check className="h-4 w-4 mr-2" /> Set
                  </Button>
                </div>
                
                {Object.keys(wagerOverrides).length > 0 ? (
                  <div className="space-y-2">
                    {Object.entries(wagerOverrides).map(([username, wagered]) => (
                      <div key={username} className="flex items-center justify-between bg-black/40 border border-purple-500/20 p-3 rounded">
                        <div>
                          <span className="font-semibold text-white">{username}</span>
                          <span className="text-yellow-400 ml-4">→ C${wagered}</span>
                        </div>
                        <Button onClick={() => handleRemoveOverride(username)} variant="destructive" size="sm">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No wager overrides</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* BONUS HUNTS TAB */}
          <TabsContent value="hunts" className="space-y-6">
            <Card className="bg-black/60 border-purple-500/30">
              <CardHeader>
                <CardTitle>Create New Bonus Hunt</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreateHunt} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Start Balance (C$)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={newHunt.start_balance}
                        onChange={(e) => setNewHunt({ ...newHunt, start_balance: e.target.value })}
                        required
                        placeholder="e.g., 5000"
                        className="bg-black/60 border-purple-500/30 text-white"
                      />
                    </div>
                    <div>
                      <Label>Status</Label>
                      <select
                        value={newHunt.status}
                        onChange={(e) => setNewHunt({ ...newHunt, status: e.target.value })}
                        className="w-full p-2 bg-gray-800 border border-gray-700 rounded"
                      >
                        <option value="preparing">Preparing</option>
                        <option value="active">Active</option>
                        <option value="completed">Completed</option>
                      </select>
                    </div>
                  </div>

                  <div className="border-t border-gray-800 pt-4">
                    <h4 className="font-bold mb-2">Add Slots</h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-4">
                      <Input
                        placeholder="Slot Name"
                        value={newSlot.name}
                        onChange={(e) => setNewSlot({ ...newSlot, name: e.target.value })}
                        className="bg-black/60 border-purple-500/30 text-white"
                      />
                      <Input
                        placeholder="Provider"
                        value={newSlot.provider}
                        onChange={(e) => setNewSlot({ ...newSlot, provider: e.target.value })}
                        className="bg-black/60 border-purple-500/30 text-white"
                      />
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Bet Size (C$)"
                        value={newSlot.bet_size}
                        onChange={(e) => setNewSlot({ ...newSlot, bet_size: e.target.value })}
                        className="bg-black/60 border-purple-500/30 text-white"
                      />
                      <Button type="button" onClick={handleAddSlot}>
                        <Plus className="h-4 w-4 mr-1" /> Add
                      </Button>
                    </div>

                    {slotsList.length > 0 && (
                      <div className="space-y-2">
                        {slotsList.map((slot, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-black/40 border border-purple-500/20 p-2 rounded">
                            <span>{slot.name} - {slot.provider} - C${slot.bet_size}</span>
                            <Button type="button" variant="destructive" size="sm" onClick={() => handleRemoveSlot(idx)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <Button type="submit" className="w-full">
                    <Save className="mr-2 h-4 w-4" /> Create Hunt
                  </Button>
                </form>
              </CardContent>
            </Card>

            {editingHunt && (
              <Card className="bg-gray-900 border-purple-500 border-2">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-purple-400">Editing Hunt #{editingHunt.hunt_id.slice(0, 8)}</CardTitle>
                  <Button variant="ghost" size="sm" onClick={handleCancelEdit}><X className="h-4 w-4" /></Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Start Balance (C$)</Label>
                      <Input
                        type="number"
                        value={editingHunt.start_balance}
                        onChange={(e) => setEditingHunt({ ...editingHunt, start_balance: parseFloat(e.target.value) })}
                        className="bg-black/60 border-purple-500/30 text-white"
                      />
                    </div>
                    <div>
                      <Label>Status</Label>
                      <select
                        value={editingHunt.status}
                        onChange={(e) => setEditingHunt({ ...editingHunt, status: e.target.value })}
                        className="w-full p-2 bg-gray-800 border border-gray-700 rounded"
                      >
                        <option value="preparing">Preparing</option>
                        <option value="active">Active</option>
                        <option value="completed">Completed</option>
                      </select>
                    </div>
                  </div>

                  {/* Auto-Calculated Stats Display */}
                  <div className="p-4 bg-gray-800 rounded-lg border border-gray-700">
                    <h4 className="font-bold text-cyan-400 mb-3 flex items-center gap-2">
                      <Calculator className="h-4 w-4" /> Hunt Stats
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-gray-300">Start Balance</p>
                        <p className="text-lg font-bold text-white">C${(editingHunt.start_balance || 0).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-300">End Balance</p>
                        <p className="text-lg font-bold text-yellow-400">C${((editingHunt.start_balance || 0) + (editingHunt.slots || []).reduce((sum, s) => sum + (s.win || 0), 0)).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-300">Best Win (Auto)</p>
                        <p className="text-lg font-bold text-green-400">C${Math.max(0, ...(editingHunt.slots || []).map(s => s.win || 0)).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-300">Run Avg X (Auto)</p>
                        <p className="text-lg font-bold text-purple-400">{editingHunt.start_balance > 0 ? ((editingHunt.slots || []).reduce((sum, s) => sum + (s.win || 0), 0) / editingHunt.start_balance).toFixed(2) : '0.00'}x</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-300">Opened (Editable)</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="0"
                            value={editingHunt.opened_count || 0}
                            onChange={(e) => setEditingHunt({ ...editingHunt, opened_count: parseInt(e.target.value) || 0 })}
                            className="bg-gray-700 border-gray-600 w-24"
                          />
                          <span className="text-gray-300">/ {(editingHunt.slots || []).length}</span>
                        </div>
                      </div>
                      <div>
                        <Label className="text-gray-300">Collected (Editable)</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="0"
                            value={editingHunt.collected_count || 0}
                            onChange={(e) => setEditingHunt({ ...editingHunt, collected_count: parseInt(e.target.value) || 0 })}
                            className="bg-gray-700 border-gray-600 w-24"
                          />
                          <span className="text-gray-300">/ {(editingHunt.slots || []).length}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-gray-700 pt-4">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="font-bold">Slots</h4>
                      <Button type="button" size="sm" onClick={handleAddSlotToExisting}>
                        <Plus className="h-4 w-4 mr-1" /> Add Slot
                      </Button>
                    </div>
                    
                    <div className="space-y-2">
                      {editingHunt.slots.map((slot, idx) => (
                        <div key={idx} className="grid grid-cols-6 gap-2 items-center bg-black/40 border border-purple-500/20 p-2 rounded">
                          <Input value={slot.name} onChange={(e) => handleUpdateSlot(idx, 'name', e.target.value)} className="bg-gray-700 text-sm" placeholder="Name" />
                          <Input value={slot.provider} onChange={(e) => handleUpdateSlot(idx, 'provider', e.target.value)} className="bg-gray-700 text-sm" placeholder="Provider" />
                          <Input type="number" value={slot.bet_size || ''} onChange={(e) => handleUpdateSlot(idx, 'bet_size', e.target.value)} className="bg-gray-700 text-sm" placeholder="Bet" />
                          <Input type="number" value={slot.win || ''} onChange={(e) => handleUpdateSlot(idx, 'win', e.target.value)} className="bg-gray-700 text-sm text-green-400" placeholder="Win" />
                          <Input type="number" value={slot.multiplier || ''} onChange={(e) => handleUpdateSlot(idx, 'multiplier', e.target.value)} className="bg-gray-700 text-sm text-yellow-400" placeholder="x" />
                          <Button type="button" variant="destructive" size="sm" onClick={() => handleRemoveSlotFromExisting(idx)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button onClick={handleSaveHunt} className="flex-1 bg-green-600 hover:bg-green-700">
                      <Check className="mr-2 h-4 w-4" /> Save
                    </Button>
                    <Button variant="outline" onClick={handleCancelEdit} className="flex-1">Cancel</Button>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="bg-black/60 border-purple-500/30">
              <CardHeader>
                <CardTitle>Existing Hunts</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {huntsList.length === 0 ? (
                    <p className="text-gray-300 text-center py-4">No hunts created yet.</p>
                  ) : (
                    huntsList.map((hunt) => {
                      const totalWins = hunt.slots?.reduce((sum, s) => sum + (s.win || 0), 0) || 0;
                      const calculatedEnd = (hunt.start_balance || 0) + totalWins;
                      const wins = hunt.slots?.map(s => s.win || 0).filter(w => w > 0) || [];
                      const bestWin = wins.length > 0 ? Math.max(...wins) : 0;
                      const runAvgX = hunt.start_balance > 0 ? (totalWins / hunt.start_balance).toFixed(2) : '0.00';
                      const totalSlots = hunt.slots?.length || 0;
                      const openedCount = hunt.opened_count || 0;
                      const collectedCount = hunt.collected_count || 0;
                      
                      return (
                        <div key={hunt.hunt_id} className="p-4 bg-gray-800 rounded">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <p className="font-bold">#{hunt.hunt_id.slice(0, 8)}</p>
                              <p className="text-sm text-gray-300">{hunt.status} - {totalSlots} slots</p>
                              
                              {/* Stats Row 1: Balance Info */}
                              <div className="flex items-center gap-2 mt-2 text-sm">
                                <span className="text-gray-300">Start:</span>
                                <span className="text-white font-medium">C${hunt.start_balance?.toLocaleString()}</span>
                                <span className="text-gray-500">|</span>
                                <span className="text-gray-300">End:</span>
                                <span className="text-yellow-400 font-bold">C${calculatedEnd.toLocaleString()}</span>
                              </div>
                              
                              {/* Stats Row 2: Best Win & Avg X */}
                              <div className="flex items-center gap-2 mt-1 text-sm">
                                <span className="text-gray-300">Best Win:</span>
                                <span className="text-green-400 font-medium">C${bestWin.toLocaleString()}</span>
                                <span className="text-gray-500">|</span>
                                <span className="text-gray-300">Avg X:</span>
                                <span className="text-purple-400 font-medium">{runAvgX}x</span>
                              </div>
                              
                              {/* Stats Row 3: Opened & Collected */}
                              <div className="flex items-center gap-2 mt-1 text-sm">
                                <span className="text-gray-300">Opened:</span>
                                <span className="text-cyan-400">{openedCount}/{totalSlots}</span>
                                <span className="text-gray-500">|</span>
                                <span className="text-gray-300">Collected:</span>
                                <span className="text-cyan-400">{collectedCount}/{totalSlots}</span>
                              </div>
                            </div>
                            <div className="flex gap-2 flex-wrap">
                              <Button size="sm" onClick={() => handleEditHunt(hunt)} className="bg-purple-600 hover:bg-purple-700">
                                <Edit className="h-4 w-4 mr-1" /> Edit
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleCopyOBSLink(hunt.hunt_id)}
                                className="bg-cyan-600 hover:bg-cyan-700"
                                data-testid={`obs-copy-btn-${hunt.hunt_id.slice(0,8)}`}
                              >
                                {copiedOBS === hunt.hunt_id ? <Check className="h-4 w-4 mr-1" /> : <Monitor className="h-4 w-4 mr-1" />}
                                {copiedOBS === hunt.hunt_id ? 'Copied!' : 'OBS Link'}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => window.open(`/obs/bonus-hunt/${hunt.hunt_id}`, '_blank')}
                                data-testid={`obs-open-btn-${hunt.hunt_id.slice(0,8)}`}
                              >
                                <ExternalLink className="h-4 w-4 mr-1" /> Preview
                              </Button>
                              <Button variant="destructive" size="sm" onClick={() => handleDeleteHunt(hunt.hunt_id)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* COMPETITIONS TAB */}
          <TabsContent value="competitions" className="space-y-6">
            {/* End Competition Modal */}
            {endCompetitionData && (
              <Card className="bg-gray-900 border-yellow-500 border-2">
                <CardHeader>
                  <CardTitle className="text-yellow-400">End Competition #{endCompetitionData.competition.competition_id.slice(0, 8)}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {endCompetitionData.hunt && (
                    <div className="p-4 bg-gray-800 rounded">
                      <p className="text-sm text-gray-300 mb-2">Calculated from Bonus Hunt:</p>
                      <div className="flex items-center gap-2">
                        <span className="text-white">C${endCompetitionData.hunt.start_balance}</span>
                        <span className="text-gray-500">+</span>
                        <span className="text-green-400">C${endCompetitionData.hunt.total_wins?.toFixed(2)}</span>
                        <span className="text-gray-500">=</span>
                        <span className="text-yellow-400 font-bold text-xl">C${endCompetitionData.calculatedEndBalance?.toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                  <div>
                    <Label>Final Balance (C$)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={endCompetitionData.finalBalance}
                      onChange={(e) => setEndCompetitionData({ ...endCompetitionData, finalBalance: e.target.value })}
                      className="bg-black/60 border-purple-500/30 text-white text-xl"
                      placeholder="Enter final balance"
                    />
                    <p className="text-xs text-gray-300 mt-1">You can adjust the calculated value if needed</p>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleEndCompetition} className="flex-1 bg-yellow-600 hover:bg-yellow-700">
                      <Trophy className="h-4 w-4 mr-2" /> End & Calculate Winner
                    </Button>
                    <Button variant="outline" onClick={() => setEndCompetitionData(null)}>Cancel</Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Competition Results Modal */}
            {competitionResults && (
              <Card className="bg-gray-900 border-green-500 border-2">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-green-400 flex items-center gap-2">
                    <Trophy className="h-6 w-6" /> Competition Results
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setCompetitionResults(null)}>
                    <X className="h-4 w-4" />
                  </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center p-4 bg-gray-800 rounded">
                    <p className="text-gray-300">Final Balance</p>
                    <p className="text-3xl font-bold text-yellow-400">C${competitionResults.final_balance?.toLocaleString()}</p>
                  </div>
                  
                  {competitionResults.winner ? (
                    <div className="p-4 bg-gradient-to-r from-yellow-900/50 to-yellow-700/30 border border-yellow-600 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Trophy className="h-10 w-10 text-yellow-400" />
                        <div>
                          <p className="text-sm text-yellow-400">WINNER</p>
                          <p className="text-2xl font-bold text-white">{competitionResults.winner.username}</p>
                          <p className="text-sm text-gray-300">
                            Guessed: C${competitionResults.winner.guess?.toLocaleString()} 
                            <span className="text-green-400 ml-2">(C${competitionResults.winner.difference?.toFixed(2)} off)</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 bg-gray-800 rounded text-center">
                      <p className="text-gray-300">No guesses were submitted</p>
                    </div>
                  )}

                  {competitionResults.all_results && competitionResults.all_results.length > 0 && (
                    <div>
                      <p className="font-bold mb-2">All Guesses ({competitionResults.total_guesses || competitionResults.all_results.length})</p>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {competitionResults.all_results.map((result, idx) => (
                          <div key={idx} className={`flex items-center justify-between p-3 rounded ${idx === 0 ? 'bg-yellow-900/30 border border-yellow-700' : 'bg-gray-800'}`}>
                            <div className="flex items-center gap-3">
                              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${idx === 0 ? 'bg-yellow-500 text-black' : 'bg-gray-700'}`}>
                                {idx + 1}
                              </span>
                              <span className="font-semibold">{result.discord_username}</span>
                            </div>
                            <div className="text-right">
                              <p className="font-bold">C${result.guess_amount?.toLocaleString()}</p>
                              <p className={`text-xs ${idx === 0 ? 'text-green-400' : 'text-gray-300'}`}>
                                C${result.difference?.toFixed(2)} off
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            <Card className="bg-black/60 border-purple-500/30">
              <CardHeader>
                <CardTitle>Start Competition</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {huntsList.map((hunt) => {
                    const totalWins = hunt.slots?.reduce((sum, s) => sum + (s.win || 0), 0) || 0;
                    const calculatedEnd = (hunt.start_balance || 0) + totalWins;
                    return (
                      <div key={hunt.hunt_id} className="flex items-center justify-between p-4 bg-gray-800 rounded">
                        <div>
                          <p className="font-bold">#{hunt.hunt_id.slice(0, 8)}</p>
                          <p className="text-sm text-gray-300">
                            Start: C${hunt.start_balance} | End: C${calculatedEnd.toFixed(2)}
                          </p>
                        </div>
                        <Button onClick={() => handleStartCompetition(hunt.hunt_id)}>Start</Button>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-black/60 border-purple-500/30">
              <CardHeader>
                <CardTitle>Competitions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {competitionsList.length === 0 ? (
                    <p className="text-gray-300 text-center py-4">No competitions yet.</p>
                  ) : (
                    competitionsList.map((comp) => (
                      <div key={comp.competition_id} className="p-4 bg-gray-800 rounded">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-bold">#{comp.competition_id.slice(0, 8)}</p>
                            <p className="text-sm text-gray-300">Status: <span className={
                              comp.status === 'active' ? 'text-green-400' : 
                              comp.status === 'paused' ? 'text-yellow-400' : 
                              comp.status === 'ended' ? 'text-red-400' : ''
                            }>{comp.status.toUpperCase()}</span></p>
                            {comp.status === 'ended' && (
                              <div className="mt-2">
                                <p className="text-sm">
                                  Final: <span className="text-yellow-400 font-bold">C${comp.final_balance?.toLocaleString()}</span>
                                </p>
                                {comp.winner_username && (
                                  <p className="text-sm text-green-400">
                                    Winner: {comp.winner_username} (guessed C${comp.winner_guess?.toLocaleString()})
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex gap-2 flex-wrap">
                            {comp.status === 'active' && (
                              <>
                                <Button onClick={() => handlePauseCompetition(comp.competition_id)} className="bg-yellow-600 hover:bg-yellow-700" data-testid="pause-competition-btn">
                                  <Pause className="h-4 w-4 mr-1" /> Pause
                                </Button>
                                <Button onClick={() => handlePrepareEndCompetition(comp)} className="bg-red-600 hover:bg-red-700" data-testid="end-competition-btn">
                                  End Competition
                                </Button>
                              </>
                            )}
                            {comp.status === 'paused' && (
                              <>
                                <Button onClick={() => handleResumeCompetition(comp.competition_id)} className="bg-green-600 hover:bg-green-700" data-testid="resume-competition-btn">
                                  <Play className="h-4 w-4 mr-1" /> Resume
                                </Button>
                                <Button onClick={() => handlePrepareEndCompetition(comp)} className="bg-red-600 hover:bg-red-700" data-testid="end-competition-btn">
                                  End Competition
                                </Button>
                              </>
                            )}
                            {comp.status === 'ended' && (
                              <Button onClick={() => handleViewResults(comp.competition_id)} variant="outline">
                                View Results
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* USERS TAB */}
          <TabsContent value="users" className="space-y-6">
            <Card className="bg-black/60 border-purple-500/30">
              <CardHeader>
                <CardTitle>Registered Users ({usersList.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {usersList.length === 0 ? (
                  <p className="text-gray-300 text-center py-8">No users yet.</p>
                ) : (
                  <div className="space-y-3">
                    {usersList.map((u) => (
                      <div key={u.user_id || u.discord_id} className="flex items-center justify-between p-4 bg-gray-800 rounded-lg">
                        <div className="flex items-center space-x-4">
                          <Avatar className="h-12 w-12">
                            <AvatarImage src={u.avatar} />
                            <AvatarFallback>{u.discord_username?.[0] || '?'}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-bold">{u.discord_username}</p>
                            <p className="text-sm text-gray-300">ID: {u.discord_id}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-300">Joined</p>
                          <p className="text-sm">{u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}</p>
                          {u.is_admin && <span className="text-xs text-yellow-500">⭐ Admin</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
